import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Upload, FileSpreadsheet, ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, X } from "lucide-react";

const TARGET_LABELS = {
  date: "Tarih",
  period: "Dönem",
  cash: "Nakit",
  card: "Kart",
  online: "Online",
  bank_transfer: "Havale",
  other: "Diğer",
  notes: "Not",
};

const NUMERIC = new Set(["cash", "card", "online", "bank_transfer", "other"]);

/**
 * Revenue Import Wizard — 3 steps:
 *  1) File pick (drag-drop CSV/XLSX)  →  POST /revenue/preview-file
 *  2) Column mapping (target field per detected header)
 *  3) Preview + validation → confirm  →  POST /revenue/import
 */
export default function RevenueImportWizard({ open, onOpenChange, onDone }) {
  const { t, lang } = useI18n();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({}); // { header: target_field }

  const reset = () => { setStep(1); setPreview(null); setMapping({}); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const uploadFile = async (file) => {
    if (!file) return;
    const okExt = /\.(csv|xlsx|xlsm|xltx)$/i.test(file.name);
    if (!okExt) {
      toast.error(lang === "tr" ? "CSV veya Excel dosyası yükle" : "CSV oder Excel-Datei");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/revenue/preview-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setMapping(data.detected || {});
      setStep(2);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0]; if (f) uploadFile(f);
  };

  const validated = (() => {
    if (!preview) return { rows: [], invalid: 0 };
    const usedTargets = Object.values(mapping).filter(Boolean);
    const dateHeader = preview.headers.find((h) => mapping[h] === "date");
    const rows = preview.rows.map((r, idx) => {
      const errs = [];
      const dateVal = dateHeader ? r[dateHeader] : "";
      if (!dateVal || !/^\d{4}-\d{2}-\d{2}$/.test(dateVal)) errs.push("date");
      // numeric fields must be parseable if mapped
      preview.headers.forEach((h) => {
        const tgt = mapping[h];
        if (tgt && NUMERIC.has(tgt)) {
          const raw = (r[h] ?? "").toString().trim();
          if (raw !== "" && Number.isNaN(parseFloat(raw.replace(",", ".")))) errs.push(tgt);
        }
      });
      return { idx, cells: r, errs };
    });
    const invalid = rows.filter((r) => r.errs.length > 0).length;
    return { rows, invalid, dateMapped: !!dateHeader, amountMapped: usedTargets.some((t) => NUMERIC.has(t)) };
  })();

  const confirmImport = async () => {
    if (!preview) return;
    if (!validated.dateMapped) return toast.error(lang === "tr" ? "Tarih sütunu eşlenmemiş" : "Datum-Spalte fehlt");
    if (!validated.amountMapped) return toast.error(lang === "tr" ? "En az bir tutar sütunu eşle" : "Mind. eine Betrag-Spalte");
    setBusy(true);
    try {
      // Build normalised rows — only valid ones
      const rows = validated.rows
        .filter((r) => r.errs.length === 0)
        .map((r) => {
          const out = {};
          preview.headers.forEach((h) => {
            const tgt = mapping[h];
            if (!tgt) return;
            const raw = (r.cells[h] ?? "").toString().trim();
            if (NUMERIC.has(tgt)) out[tgt] = parseFloat(raw.replace(",", ".")) || 0;
            else out[tgt] = raw;
          });
          return out;
        });
      if (rows.length === 0) return toast.error(lang === "tr" ? "Geçerli satır yok" : "Keine gültige Zeile");
      const { data } = await api.post("/revenue/import", { rows });
      toast.success(`+${data.created} · ↻${data.updated} ${lang === "tr" ? "satır" : "Zeilen"}`);
      close(false); onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl rounded-xl" data-testid="rev-import-wizard">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary" />
            {lang === "tr" ? "Ciro İçe Aktarma" : "Umsatz-Import"}
            <StepPill n={1} active={step === 1} label={lang === "tr" ? "Dosya" : "Datei"} />
            <StepPill n={2} active={step === 2} label={lang === "tr" ? "Eşleme" : "Zuordnung"} />
            <StepPill n={3} active={step === 3} label={lang === "tr" ? "Önizleme" : "Vorschau"} />
          </DialogTitle>
        </DialogHeader>

        {/* Step 1 — File picker */}
        {step === 1 && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
            className={`rounded-xl border-2 border-dashed p-10 text-center transition-colors ${drag ? "border-primary bg-primary/5" : "border-border bg-muted/20"}`}
            data-testid="rev-drop"
          >
            <div className="w-14 h-14 rounded-2xl bg-background border shadow-elev-1 flex items-center justify-center mx-auto mb-4">
              <Upload className="w-6 h-6 text-muted-foreground" />
            </div>
            <div className="font-display font-semibold">
              {lang === "tr" ? "CSV veya Excel dosyanı sürükle" : "CSV / Excel hierher ziehen"}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {lang === "tr" ? "Sütun başlıklarını otomatik algılarız · .csv, .xlsx" : "Spalten werden auto-erkannt · .csv, .xlsx"}
            </p>
            <label className="mt-5 inline-flex">
              <input
                type="file"
                accept=".csv,.xlsx,.xlsm,.xltx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => uploadFile(e.target.files?.[0])}
                className="hidden"
                data-testid="rev-file-input"
              />
              <Button asChild variant="outline" className="h-10 rounded-lg cursor-pointer" disabled={busy}>
                <span>{busy ? t("loading") : (lang === "tr" ? "Dosya seç" : "Datei wählen")}</span>
              </Button>
            </label>
          </div>
        )}

        {/* Step 2 — Column mapping */}
        {step === 2 && preview && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display font-semibold">{preview.filename}</div>
                <div className="text-xs text-muted-foreground font-mono">{preview.row_count} {lang === "tr" ? "satır" : "Zeilen"} · {preview.headers.length} {lang === "tr" ? "sütun" : "Spalten"}</div>
              </div>
              <div className="text-xs text-muted-foreground">
                {lang === "tr" ? "Her sütunu eşle (algılanan seçili)" : "Jede Spalte zuordnen"}
              </div>
            </div>
            <div className="border rounded-xl overflow-hidden shadow-elev-1 max-h-72 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{lang === "tr" ? "Dosya sütunu" : "Datei-Spalte"}</th>
                    <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{lang === "tr" ? "Örnek" : "Beispiel"}</th>
                    <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{lang === "tr" ? "Eşle" : "Zuordnung"}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {preview.headers.map((h) => (
                    <tr key={h}>
                      <td className="px-4 py-2 font-mono text-xs">{h}</td>
                      <td className="px-4 py-2 font-mono text-xs text-muted-foreground truncate max-w-[200px]">{preview.rows[0]?.[h] ?? "—"}</td>
                      <td className="px-4 py-2">
                        <Select value={mapping[h] || "__none"} onValueChange={(v) => setMapping({ ...mapping, [h]: v === "__none" ? "" : v })}>
                          <SelectTrigger className="h-8 rounded-lg w-52" data-testid={`map-${h}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">{lang === "tr" ? "— Yok say —" : "— ignorieren —"}</SelectItem>
                            {(preview.target_fields || Object.keys(TARGET_LABELS)).map((f) => (
                              <SelectItem key={f} value={f}>{TARGET_LABELS[f] || f}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Step 3 — Preview */}
        {step === 3 && preview && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                {validated.invalid > 0 ? (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 rounded-md">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    {validated.invalid} {lang === "tr" ? "hatalı" : "ungültig"}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 rounded-md">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {lang === "tr" ? "Tüm satırlar geçerli" : "Alle Zeilen gültig"}
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground font-mono">{validated.rows.length - validated.invalid} / {validated.rows.length} {lang === "tr" ? "aktarılacak" : "importierbar"}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                {lang === "tr" ? "Aynı tarih için satır varsa güncellenir" : "Bestehende Zeilen werden aktualisiert"}
              </div>
            </div>
            <div className="border rounded-xl overflow-hidden shadow-elev-1 max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 sticky top-0">
                  <tr>
                    <th className="w-8 px-2 py-2.5"></th>
                    {preview.headers.map((h) => (
                      <th key={h} className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                        <div>{h}</div>
                        {mapping[h] && <div className="text-[10px] text-primary lowercase">→ {mapping[h]}</div>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {validated.rows.map((r) => (
                    <tr key={r.idx} className={r.errs.length > 0 ? "bg-red-50/50" : ""} data-testid={`rev-row-${r.idx}`}>
                      <td className="text-center px-2 py-2">
                        {r.errs.length > 0
                          ? <X className="w-3.5 h-3.5 text-destructive inline-block" />
                          : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 inline-block" />}
                      </td>
                      {preview.headers.map((h) => (
                        <td key={h} className={`px-3 py-2 font-mono text-xs ${r.errs.includes(mapping[h]) ? "text-destructive" : ""}`}>
                          {r.cells[h] || "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep(step - 1)} className="rounded-lg" data-testid="rev-back">
              <ChevronLeft className="w-4 h-4 mr-1" /> {lang === "tr" ? "Geri" : "Zurück"}
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="ghost" onClick={() => close(false)} className="rounded-lg">{t("cancel")}</Button>
          {step === 2 && (
            <Button onClick={() => setStep(3)} className="rounded-lg shadow-elev-2" disabled={!validated.dateMapped || !validated.amountMapped} data-testid="rev-next">
              {lang === "tr" ? "Devam" : "Weiter"} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          {step === 3 && (
            <Button onClick={confirmImport} className="rounded-lg shadow-elev-2" disabled={busy || validated.rows.length - validated.invalid === 0} data-testid="rev-confirm">
              {busy ? t("loading") : (lang === "tr" ? `İçe aktar (${validated.rows.length - validated.invalid})` : `Importieren (${validated.rows.length - validated.invalid})`)}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepPill({ n, active, label }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md ml-1 ${active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
      <span className="font-mono">{n}</span> {label}
    </span>
  );
}
